import type { Message } from '@ai-sdk/react';
import { serializer } from '@copera/tiptap/markdown';
import { useConversationList } from '@modules/ai-conversation/hooks/useConversationList';
import { Editor } from '@tiptap/core';
import { useEffect, useMemo, useRef } from 'react';
import type { AiPromptTemplate } from 'src/@types/ai';
import { getExtensionOption } from '../../utils/getExtensionOption';
import { useAiPromptTemplateChat } from './useAiPromptTemplateChat';
import { useSendAIPromptMessage } from './useSendAIPromptMessage';

export const useAiPrompt = (editor: Editor) => {
  const { newConversationControl: control } = useConversationList();

  const {
    inputValue,
    setInputValue,
    setCurrentDocument,
    setSelectedContext,
    selectedContext,
  } = control((s) => ({
    inputValue: s.inputValue,
    setInputValue: s.setInputValue,
    setCurrentDocument: s.setCurrentDocument,
    setSelectedContext: s.setSelectedContext,
    selectedContext: s.selectedContext,
  }));
  const selectionRef = useRef(editor.state.selection);

  const configs = useMemo(
    () => getExtensionOption(editor, 'aiPromptOptions'),
    [editor],
  );
  useEffect(() => {
    setCurrentDocument(configs?.currentDocument || null);
  }, [configs?.currentDocument, setCurrentDocument]);

  useEffect(() => {
    selectionRef.current = editor.state.selection;
    const slice = editor.state.doc
      .slice(selectionRef.current.from, selectionRef.current.to)
      .toJSON();
    if (slice?.content) {
      const doc = editor.schema.nodeFromJSON({
        type: 'doc',
        content: slice.content,
      });
      setSelectedContext(serializer.serialize(doc).trim());
    } else {
      setSelectedContext('');
    }
  }, [editor, setSelectedContext]);

  const { getResponseForTemplate } = useAiPromptTemplateChat();
  const { sendMessage, messages, appendMessage, status, stop } =
    useSendAIPromptMessage(control);

  const handleSendChatMessage = async (message: string) => {
    setInputValue('');
    await sendMessage(message);
  };

  const getResponse = async (
    template: AiPromptTemplate,
    input = inputValue,
  ) => {
    appendMessage({
      role: 'user',
      content: input,
    } as Message);
    setInputValue('');
    const response = await getResponseForTemplate({
      template,
      input,
      context: selectedContext,
    });
    appendMessage({
      role: 'assistant',
      content: response,
    } as Message);
  };

  return {
    messages,
    getResponse,
    handleSendChatMessage,
    selectionRef,
    control,
    configs,
    status,
    stop,
  };
};

export type UseAiPrompt = ReturnType<typeof useAiPrompt>;
